import React, { useState } from "react";
import {
  Box,
  Button,
  TextInput,
  Layer,
  Heading,
  Notification,
  Spinner,
  Text,
} from "grommet";
import axios from "axios";
import API_URL from "../../config/config";
import { StatusWarning } from "grommet-icons";

export const SendInvite = ({ manager_id, groupId, onUserAdded, users }) => {
  const group_id = groupId || "";
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isManagerFlag, setIsManagerFlag] = useState(false);
  const [isManagerWarning, setIsManagerWarning] = useState(false);
  const checkValidMail = (email) => {
    const re =
      /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i;
    return re.test(email);
  };

  // Handle invitation
  const handleInvite = async () => {
    if (!email) {
      setError("Email is required");
      return;
    }
    if (!checkValidMail(email)) {
      setError("Invalid email address");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Invite the user via API
      const res = await axios.post(`${API_URL}/manager/invite_user`, {
        email,
        groupId: group_id || 0, // Default groupId to 0 if not provided
        manager_id,
      });
      const manager_flag = res.data.isManagerFlag;
      setIsManagerFlag(manager_flag);
      setIsManagerWarning(manager_flag);
      if (!manager_flag) {
        // Display success notification
        setShowSuccess(true);

        // Clear the invite form
        setEmail("");
        setShowInviteForm(false);

        // Notify parent component to reload data
        if (onUserAdded) {
          await onUserAdded();
        }
      }
    } catch (error) {
      console.error("Error inviting user:", error);
      setError(
        error.response?.data?.message ||
          "Failed to invite user. Please try again.",
      );
    } finally {
      setLoading(false);
      setTimeout(() => setShowSuccess(false), 4000); // Hide success message after 4 seconds
    }
  };
  const handleContinue = async () => {
    // Display success notification
    setShowSuccess(true);

    // Clear the invite form
    setEmail("");
    setShowInviteForm(false);

    // Notify parent component to reload data
    if (onUserAdded) {
      await onUserAdded();
    }
  };

  if (isManagerWarning) {
    return (
      <Box>
        <Layer
          border={{ color: "status-warning", size: "small" }}
          round="small"
          elevation="medium"
        >
          <Box
            pad="medium"
            // background="status-warning"

            margin={{ vertical: "small" }}
            direction="column"
          >
            <Box
              direction="row"
              align="center"
              gap="small"
              alignContent="center"
            >
              <StatusWarning color="brand" size="medium" />
              <Text size="medium" weight="bold" color="brand">
                Notice: Invite Acceptance Not Guaranteed
              </Text>
            </Box>
            <Box>
              <Text size="medium">
                The user you are inviting is currently a manager. As a result,
                they may choose to accept or decline the invitation based on
                their existing role and responsibilities.
                <br /> Please be aware that their acceptance is not guaranteed.
                Consider informing them in advance to ensure alignment before
                sending the invite.
              </Text>
            </Box>
            <Box align="end">
              <Button
                label="Continue"
                onClick={handleContinue}
                primary
                margin={{ top: "medium" }}
              />
            </Box>
          </Box>
        </Layer>
      </Box>
    );
  }
  return (
    <Box>
      <Button
        label="Invite User"
        onClick={() => setShowInviteForm(true)}
        secondary
        disabled={users === 10}
      />

      {/* Invite Form Modal */}
      {showInviteForm && (
        <Layer
          onEsc={() => setShowInviteForm(false)}
          onClickOutside={() => setShowInviteForm(false)}
        >
          <Box pad="medium" gap="medium" width="medium">
            <Heading level="3" margin="none">
              Invite a User
            </Heading>
            <TextInput
              placeholder="Enter user email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            {error && <Text color="status-critical">{error}</Text>}

            <Box
              direction="row"
              gap="small"
              justify="end"
              margin={{ top: "small" }}
            >
              <Button label="Cancel" onClick={() => setShowInviteForm(false)} />
              <Button label="Send Invite" onClick={handleInvite} primary />
            </Box>
          </Box>
        </Layer>
      )}

      {/* Loading Spinner */}
      {loading && (
        <Layer>
          <Box pad="medium" align="center" gap="small">
            <Spinner />
            <Text>Sending Invite...</Text>
          </Box>
        </Layer>
      )}

      {/* Success Notification */}
      {showSuccess && (
        <Notification
          toast
          status="normal"
          title="User Invited"
          message="The user was successfully invited."
          onClose={() => setShowSuccess(false)}
        />
      )}
    </Box>
  );
};
